/**
 * @package   FlorenceEGI — EGI — CoA PDF Thumbnail (M-EGI-351 FASE 3)
 * @author    Padmin D. Curtis (AI Partner OS3.0-Compliant) for Fabio Cherici
 * @version   1.0.0
 * @date      2026-07-01
 * @purpose   Modulo TS CONDIVISO e riusabile per l'anteprima (thumbnail) del PDF di un CoA.
 *            Usato da DUE consumer senza duplicazione:
 *              (1) egis-show pubblica — anteprima collassabile del CoA attivo (coa-thumb-island.ts);
 *              (2) wizard "Le tue versioni" (privato creator) — mini-thumb accanto all'evidenza
 *                  del CoA-versione appena creato (coa-flow.ts / versions.ts).
 *            La LOGICA TESTABILE (verifica esistenza PDF, guard URL, decisione render/fallback,
 *            costruzione container) è isolata qui; il render canvas vero (pdf.js) gira solo in
 *            browser (jsdom non ha canvas 2d né WebGL) e va verificato manualmente.
 *
 * Sicurezza (P0 / OWASP): nessun markup grezzo con input dinamico — solo textContent/createElement;
 *            gli URL (checkUrl, pdfUrl) passano per isSafePdfUrl (whitelist http(s)/path assoluto,
 *            anti javascript:/data: — CWE-79/CWE-84). Il PDF si apre in nuova scheda con noopener.
 * A11Y (WCAG 2.2): container a dimensioni FISSE (aspect-ratio + width) → nessun CLS (no layout shift);
 *            render on-demand (lazy) → nessun costo CWV finché non richiesto; canvas con role="img"
 *            + aria-label i18n; il click sull'anteprima apre il PDF (target ≥ 24px, §2.5.8 AA).
 * pdf.js:    caricato via CDN v3.11.174 (loader ripreso dal codice pre-M-EGI-268, con cache-busting).
 *            NB (da decidere CEO): `pdfjs-dist` NON è in package.json — bundlarlo via Vite sarebbe
 *            preferibile (no dipendenza runtime da CDN, CSP più stretta, versioning). Finché il CEO
 *            non approva la dipendenza, si resta sul loader CDN come nel codice legacy.
 */

/** Etichette i18n (nessun testo hardcoded — P0-2/P0-9). */
export interface PdfThumbI18n {
  /** aria-label del canvas/anteprima (es. "Anteprima PDF del certificato"). */
  readonly ariaLabel: string;
  /** Messaggio quando il PDF non è (ancora) disponibile. */
  readonly unavailable: string;
  /** Messaggio di errore generico (render fallito / rete). */
  readonly error: string;
}

/** Opzioni di renderPdfThumb. */
export interface PdfThumbOptions {
  /**
   * URL dell'endpoint di verifica esistenza PDF (GET → JSON {pdf_exists, pdf_url|download_url}).
   * OPZIONALE: se `pdfUrl` è già noto (es. dalla response del POST, o CoA attivo server-side),
   * si può passare solo `pdfUrl` e saltare il check (evita una chiamata auth-gated inutile).
   */
  readonly checkUrl?: string;
  /** URL diretto del PDF (bypassa il check). Ha precedenza su checkUrl se entrambi presenti. */
  readonly pdfUrl?: string;
  /** Larghezza target del thumbnail in px (default 140). L'altezza deriva dall'aspect-ratio. */
  readonly width?: number;
  /** CSRF token (necessario solo se si usa checkUrl su endpoint autenticato). */
  readonly csrfToken?: string;
  /** Etichette i18n. */
  readonly i18n: PdfThumbI18n;
}

/** Esito della fase di risoluzione dell'URL PDF (logica pura, testabile). */
export interface PdfResolveResult {
  /** URL sicuro del PDF da renderizzare, oppure null se non disponibile/non sicuro. */
  readonly pdfUrl: string | null;
}

/** URL sicuro per il PDF: http(s) o path assoluto ("/..."). Blocca javascript:/data: (CWE-79/84). */
export function isSafePdfUrl(url: string | null | undefined): boolean {
  const trimmed = (url ?? "").trim();
  if (trimmed === "") return false;
  if (trimmed.startsWith("/")) return true;
  try {
    const u = new URL(trimmed, "https://placeholder.invalid");
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/** Forma minima attesa dalla response di /pdf/check (tipata ai confini di rete — no-any). */
interface PdfCheckResponse {
  readonly pdf_exists?: boolean;
  readonly pdf_url?: string;
  readonly download_url?: string;
}

function isPdfCheckResponse(value: unknown): value is PdfCheckResponse {
  return typeof value === "object" && value !== null;
}

/**
 * LOGICA PURA (testabile): risolve l'URL del PDF da renderizzare.
 * Precedenza a `pdfUrl` diretto (se sicuro); altrimenti interroga `checkUrl` e valida la risposta.
 * Ritorna sempre un URL SICURO o null (mai un URL non whitelisted).
 * fetch è iniettabile per i test (default: fetch globale).
 */
export async function resolvePdfUrl(
  opts: Pick<PdfThumbOptions, "checkUrl" | "pdfUrl" | "csrfToken">,
  fetchImpl: typeof fetch = fetch,
): Promise<PdfResolveResult> {
  // (1) URL diretto già noto → nessuna chiamata di rete.
  if (opts.pdfUrl !== undefined) {
    return { pdfUrl: isSafePdfUrl(opts.pdfUrl) ? opts.pdfUrl.trim() : null };
  }

  // (2) Nessun URL diretto e nessun check → niente da renderizzare.
  if (opts.checkUrl === undefined || !isSafePdfUrl(opts.checkUrl)) {
    return { pdfUrl: null };
  }

  // (3) Interroga l'endpoint di verifica.
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (opts.csrfToken !== undefined && opts.csrfToken !== "") {
      headers["X-CSRF-TOKEN"] = opts.csrfToken;
    }
    const res = await fetchImpl(opts.checkUrl, { headers });
    if (!res.ok) return { pdfUrl: null };
    const data: unknown = await res.json().catch(() => null);
    if (!isPdfCheckResponse(data) || data.pdf_exists !== true) {
      return { pdfUrl: null };
    }
    const url = data.pdf_url ?? data.download_url ?? "";
    return { pdfUrl: isSafePdfUrl(url) ? url.trim() : null };
  } catch {
    return { pdfUrl: null };
  }
}

/** Stati visivi del container (attributo data per test/CSS, nessun markup grezzo dinamico). */
type ThumbState = "loading" | "unavailable" | "error" | "ready";

/** Imposta lo stato + un messaggio testuale (textContent, mai markup grezzo). */
function setState(container: HTMLElement, state: ThumbState, text?: string): void {
  container.dataset.coaThumbState = state;
  if (text !== undefined) {
    // Rimpiazza il contenuto in modo XSS-safe: svuota poi appende un nodo di testo.
    container.replaceChildren();
    const msg = document.createElement("div");
    msg.className = "p-3 text-center text-xs text-gray-400";
    msg.textContent = text;
    container.append(msg);
  }
}

/** Stato "unavailable" (PDF non presente) — messaggio i18n pulito, nessun crash. */
function showUnavailable(container: HTMLElement, i18n: PdfThumbI18n): void {
  setState(container, "unavailable", i18n.unavailable);
}

/** Stato "error" (render/rete fallita) — messaggio i18n pulito, nessun crash. */
function showError(container: HTMLElement, i18n: PdfThumbI18n): void {
  setState(container, "error", i18n.error);
}

/** True se l'ambiente sa disegnare su canvas 2d (jsdom / no-canvas → false → skip pulito). */
function canRenderCanvas(): boolean {
  try {
    const c = document.createElement("canvas");
    return typeof c.getContext === "function" && c.getContext("2d") !== null;
  } catch {
    return false;
  }
}

// ── pdf.js CDN loader (ripreso dal codice pre-M-EGI-268) ────────────────────
// NB: preferibile bundlare `pdfjs-dist` via Vite (decisione dipendenza → CEO). Vedi @purpose.
const PDFJS_VERSION = "3.11.174";
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;

interface PdfJsLib {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(src: { url: string }): { promise: Promise<PdfDocumentProxy> };
}
interface PdfDocumentProxy {
  getPage(n: number): Promise<PdfPageProxy>;
}
interface PdfViewport {
  width: number;
  height: number;
}
interface PdfPageProxy {
  getViewport(o: { scale: number }): PdfViewport;
  render(o: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }): {
    promise: Promise<void>;
  };
}

let pdfjsReady: Promise<PdfJsLib> | null = null;

function getPdfJs(): PdfJsLib | undefined {
  return (window as unknown as { pdfjsLib?: PdfJsLib }).pdfjsLib;
}

/** Carica pdf.js da CDN una sola volta (idempotente). Cache-busting via ?v=<versione>. */
function ensurePdfJsLoaded(): Promise<PdfJsLib> {
  const existing = getPdfJs();
  if (existing) return Promise.resolve(existing);
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise<PdfJsLib>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${PDFJS_BASE}/pdf.min.js?v=${PDFJS_VERSION}`;
    s.async = true;
    s.onload = (): void => {
      const lib = getPdfJs();
      if (!lib) {
        reject(new Error("pdfjsLib not available after load"));
        return;
      }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js?v=${PDFJS_VERSION}`;
      resolve(lib);
    };
    s.onerror = (): void => reject(new Error("pdf.js CDN load failed"));
    document.head.appendChild(s);
  });
  return pdfjsReady;
}

/**
 * Renderizza la prima pagina del PDF nel container come thumbnail cliccabile.
 * Flusso: (1) risolve l'URL sicuro (pdfUrl diretto o via checkUrl) → se null: fallback "unavailable";
 *         (2) se il canvas non è disponibile (jsdom/headless senza 2d) → skip pulito (fallback);
 *         (3) carica pdf.js, renderizza pagina 1 su canvas dimensionato a `width`;
 *         (4) click → apre il PDF in nuova scheda (noopener).
 * Qualunque fallimento → fallback i18n, MAI un throw non gestito (nessun crash della pagina).
 * fetchImpl iniettabile per i test.
 */
export async function renderPdfThumb(
  container: HTMLElement,
  opts: PdfThumbOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const width = opts.width ?? 140;
  setState(container, "loading");

  const { pdfUrl } = await resolvePdfUrl(opts, fetchImpl);
  if (pdfUrl === null) {
    showUnavailable(container, opts.i18n);
    return;
  }

  // Ambiente senza canvas 2d (jsdom / headless senza GPU-fallback): non tentiamo il render,
  // ma offriamo comunque l'accesso al PDF (link) — nessun crash.
  if (!canRenderCanvas()) {
    setState(container, "ready");
    container.replaceChildren();
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.className = "block p-3 text-center text-xs text-amber-300 underline";
    link.textContent = opts.i18n.ariaLabel;
    container.append(link);
    return;
  }

  try {
    const lib = await ensurePdfJsLoaded();
    const pdf = await lib.getDocument({ url: pdfUrl }).promise;
    const page = await pdf.getPage(1);

    const base = page.getViewport({ scale: 1 });
    const scale = width / base.width;
    const vp = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(vp.width);
    canvas.height = Math.floor(vp.height);
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", opts.i18n.ariaLabel);
    canvas.style.width = "100%";
    canvas.style.height = "auto";
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
      showError(container, opts.i18n);
      return;
    }
    await page.render({ canvasContext: ctx, viewport: vp }).promise;

    setState(container, "ready");
    container.replaceChildren(canvas);
    // Click → apre il PDF (once: nessuna accumulazione di listener sul re-render).
    container.addEventListener(
      "click",
      () => window.open(pdfUrl, "_blank", "noopener"),
      { once: true },
    );
  } catch {
    showError(container, opts.i18n);
  }
}
